import React, {Component, ErrorInfo, ReactNode} from 'react';

import {ERROR_CONTENT, SystemError} from '@components/SystemError';
import {generateErrorId} from '@utils/errorId';

interface ErrorBoundaryProps {
    children: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    errorId: string | null;
}

function isChunkError(error: Error): boolean {
    return (
        error.name === 'ChunkLoadError' ||
        error.message.includes('Loading chunk') ||
        error.message.includes('Failed to fetch dynamically imported module')
    );
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = {hasError: false, errorId: null};

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        if (isChunkError(error)) {
            const alreadyRetried = sessionStorage.getItem('chunk_reload');

            if (!alreadyRetried) {
                sessionStorage.setItem('chunk_reload', '1');
                window.location.reload();
            }
        }

        return {hasError: true, errorId: generateErrorId()};
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error(error, errorInfo);
    }

    componentDidMount() {
        this.clearChunkReloadFlag();
    }

    componentDidUpdate() {
        this.clearChunkReloadFlag();
    }

    clearChunkReloadFlag() {
        if (!this.state.hasError) {
            sessionStorage.removeItem('chunk_reload');
        }
    }

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        const content = ERROR_CONTENT.unknown;

        return (
            <SystemError
                imageSrc={content.imageSrc}
                title={content.title}
                description={content.description}
                errorId={this.state.errorId || undefined}
                variant={content.variant}
                onReload={() => window.location.reload()}
                onHome={() => window.location.assign('/home')}
            />
        );
    }
}
